import { defineComponent as _defineComponent } from 'vue'
import { createVNode as _createVNode, renderSlot as _renderSlot, resolveDynamicComponent as _resolveDynamicComponent, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, withCtx as _withCtx, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, Fragment as _Fragment, createElementBlock as _createElementBlock } from "vue"

import CollapsibleSections from '@/components/common/collapsible-section/CollapsibleSections.vue';
import BotMessageHeader from '@/components/common/bot-message-header/BotMessageHeader.vue';
import ContextualMenuSelection from '@/components/common/contextual-menu-selection/ContextualMenuSelection.vue';
import FloatingCard from '@/components/common/floating-card/FloatingCard.vue';
import MarkDownComponent from '@/components/common/mark-down/MarkDownComponent.vue';
import ConversationActions from '@/components/common/conversation-actions/ConversationActions.vue';
import { useToasterStore } from '@/stores/useToasterStore';
import { OfficeUtils } from '@/utils/office.utils';
import { onMounted, onUnmounted, ref, computed, VNode } from 'vue';
import { getSectionPriority } from '@/core/sectionPriority';
import { useI18n } from 'vue-i18n';
import { BaseMessage, LegalAssistantSectionsMessage } from '@/models/messages';
import { isValidAnswer } from '@/utils/answer-validation.utils';

interface Props {
  message: BaseMessage;
  botMessageTitle: string;
  hideActions?: boolean;
  botMessageFooter?: (message: BaseMessage) => VNode | null;
  productIndex?: number;
  defaultSection?: string;
}


export default /*@__PURE__*/_defineComponent({
  __name: 'ChatMessage',
  props: {
    message: {},
    botMessageTitle: {},
    hideActions: { type: Boolean },
    botMessageFooter: { type: Function },
    productIndex: {},
    defaultSection: {}
  },
  setup(__props: any) {

const props = __props;

onMounted(() => {
  document.addEventListener('selectionchange', handleSelectionChange);
  document.addEventListener('click', handleClickOutside);
});

onUnmounted(() => {
  document.removeEventListener('selectionchange', handleSelectionChange);
  document.removeEventListener('click', handleClickOutside);
});

const { t } = useI18n();
const toaster = useToasterStore();

const showMenu = ref<boolean>(false);
const menuPosition = ref<{ x: number; y: number }>({ x: 0, y: 0 });
const selectedText = ref<string>('');

const sectionResponses = computed(
  () => (props.message as LegalAssistantSectionsMessage).sectionResponses || {}
);
const sectionSources = computed(
  () => (props.message as LegalAssistantSectionsMessage).sectionSources || {}
);

const shouldShowSections = computed(() => props.message.text !== '');
const sectionPriority = computed(() =>
  getSectionPriority(props.productIndex ?? 0)
);
const defaultSection = computed(() => props.defaultSection ?? null);

const orderedSections = computed(() =>
  sectionPriority.value.filter((section: string) => {
    const answer = sectionResponses.value[section];
    if (defaultSection.value && section === defaultSection.value) {
      return false;
    }
    return section in sectionResponses.value && isValidAnswer(answer);
  })
);

function onIconClick(action: 'copy' | 'insert' | 'clip'): void {
  switch (action) {
    case 'copy':
      copyTextToClipboard(props.message.text);
      break;
    case 'insert':
      insertTextToDocument(props.message.text);
      break;
    case 'clip':
      break;
    default:
      console.error('Action not recognized');
  }
}

async function copyTextToClipboard(text: string): Promise<void> {
  try {
    await OfficeUtils.copyTextToClipboard(text);
    toaster.success({
      title: t('actions.toast.text_copied')
    });
  } catch {
    toaster.error({
      title: t('actions.toast.text_copied.error')
    });
  } finally {
    resetContextMenu();
  }
}

async function insertTextToDocument(text: string): Promise<void> {
  try {
    await OfficeUtils.insertTextToDocument(text);
    toaster.success({
      title: t('actions.toast.text_inserted')
    });
  } catch {
    toaster.error({
      title: t('actions.toast.text_inserted.error')
    });
  } finally {
    resetContextMenu();
  }
}

function handleClickOutside(): void {
  const selection = window.getSelection();

  if (!selection || selection.toString().trim() === '') {
    showMenu.value = false;
  }
}

function handleMouseUp(event: MouseEvent): void {
  if (selectedText.value) {
    showMenu.value = true;
    menuPosition.value = { x: event.pageX, y: event.pageY };
  }
}

function handleSelectionChange(): void {
  const selection = window.getSelection();

  if (selection && selection.toString().trim() !== '') {
    selectedText.value = selection.toString();
  } else {
    selectedText.value = '';
    showMenu.value = false;
  }
}

const isAllowedClipboard = (): boolean =>
  Office.context.platform !== Office.PlatformType.OfficeOnline ||
  OfficeUtils.isOutlook();

const resetContextMenu = (): void => {
  if (showMenu.value) {
    showMenu.value = false;
    window.getSelection()?.removeAllRanges();
  }
};

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock(_Fragment, null, [
    _createElementVNode("div", { onMouseup: handleMouseUp }, [
      (_ctx.message.isBot)
        ? (_openBlock(), _createBlock(FloatingCard, { key: 0 }, {
            default: _withCtx(() => [
              _createVNode(BotMessageHeader, {
                dateTime: _ctx.message.dateTime?.toString(),
                title: _ctx.botMessageTitle
              }, null, 8, ["dateTime", "title"]),
              _createVNode(MarkDownComponent, { message: _ctx.message }, null, 8, ["message"]),
              (_ctx.botMessageFooter)
                ? _renderSlot(_ctx.$slots, "default", { key: 0 }, () => [
                    (_openBlock(), _createBlock(_resolveDynamicComponent(_ctx.botMessageFooter(_ctx.message))))
                  ])
                : _createCommentVNode("", true),
              (!props.hideActions)
                ? (_openBlock(), _createBlock(ConversationActions, {
                    key: 1,
                    isAllowedClipboard: isAllowedClipboard,
                    onAction: onIconClick
                  }))
                : _createCommentVNode("", true),
              (shouldShowSections.value && orderedSections.value.length)
                ? (_openBlock(), _createBlock(CollapsibleSections, {
                    key: 2,
                    showSections: shouldShowSections.value,
                    orderedSections: orderedSections.value,
                    sectionResponses: sectionResponses.value,
                    sectionSources: sectionSources.value
                  }, null, 8, ["showSections", "orderedSections", "sectionResponses", "sectionSources"]))
                : _createCommentVNode("", true)
            ]),
            _: 3
          }))
        : (_openBlock(), _createBlock(FloatingCard, {
            key: 1,
            class: "background-color-secondary-200",
            withShadow: false,
            withBorder: false
          }, {
            default: _withCtx(() => [
              _createElementVNode("p", null, _toDisplayString(_ctx.message.text), 1)
            ]),
            _: 1
          }))
    ], 32),
    _createVNode(ContextualMenuSelection, {
      show: showMenu.value,
      position: menuPosition.value,
      text: selectedText.value,
      onCopy: _cache[0] || (_cache[0] = ($event: any) => (copyTextToClipboard($event))),
      onInsert: _cache[1] || (_cache[1] = ($event: any) => (insertTextToDocument($event)))
    }, null, 8, ["show", "position", "text"])
  ], 64))
}
}

})